import React, { lazy, Suspense } from 'react';
import { Route, Switch } from 'react-router-dom';

import { LoginRequired } from '@components/LoginRequired';
import { NotFoundView } from '@views/NotFoundView';
import { UserRouter } from './User';
import { OfflineView } from '@views/OfflineView';
import { P24ReturnPage } from '@views/Offer/Payment/P24ReturnPage';
import { PrivacyPolicyView } from '@views/PrivacyPolicy';
import { RegulationView } from '@views/Regulation';
import { ServerErrorView } from '@views/ServerError';
import {
  directAccessUrl,
  driverLicenseUrl,
  homeUrl,
  loginUrl,
  notFoundUrl,
  offerBasePath,
  offerCreateUrl,
  offerDetailUrl,
  offerListingUrl,
  offlineUrl,
  p24ReturnUrl,
  passwordResetUrl,
  passwordSetUrl,
  priceListUrl,
  privacyPolicyUrl,
  registerUrl,
  regulationUrl,
  serverErrorUrl,
  userBasePath,
} from '@urls';
import useEnv, { ENV } from '@utils/env';

// Lazy only for heavy routes (Offer, DirectAccess, User, Blog, auth flows)
const HomePageView = lazy(() => import('@views/Home').then(module => ({ default: module.HomePageView })));
const PriceListView = lazy(() => import('@views/PriceList').then(module => ({ default: module.PriceListView })));
const DirectAccessView = lazy(() => import('@views/DirectAccess').then(module => ({ default: module.DirectAccessView })));
const LoginView = lazy(() => import('@views/Login').then(module => ({ default: module.LoginView })));
const RegisterView = lazy(() => import('@views/Register').then(module => ({ default: module.RegisterView })));
const PasswordResetView = lazy(() => import('@views/PasswordReset').then(module => ({ default: module.PasswordResetView })));
const OfferListingView = lazy(() => import('@views/OfferListing').then(module => ({ default: module.OfferListingView })));
const OfferCreateView = lazy(() => import('@views/Offer').then(module => ({ default: module.OfferCreateView })));
const OfferRouter = lazy(() => import('./Offer').then(module => ({ default: module.OfferRouter })));

const withLogin = (Component: React.ComponentType<any>) => (routeProps: any) => (
  <LoginRequired>
    <Component {...routeProps} />
  </LoginRequired>
);

export const AppRouter = () => {
  const env = useEnv();

  return (
    <Suspense fallback={null}>
      <Switch>
        {/* Guard: catches /oferta/nowa/ before offerDetailUrl to prevent route collision */}
        <Route exact path={offerCreateUrl} render={withLogin(OfferCreateView)} />
        {/* Single Route for home + driver license + offer detail — prevents remount on first offer click */}
        <Route exact path={[homeUrl, driverLicenseUrl, offerDetailUrl]} component={HomePageView} />
        <Route exact path={loginUrl} component={LoginView} />
        <Route exact path={registerUrl} component={RegisterView} />
        <Route exact path={[passwordSetUrl, passwordResetUrl]} component={PasswordResetView} />
        <Route exact path={regulationUrl} component={RegulationView} />
        <Route exact path={p24ReturnUrl} component={P24ReturnPage} />
        <Route exact path={offerListingUrl} component={OfferListingView} />
        <Route path={offerBasePath} component={OfferRouter} />
        <Route path={userBasePath} component={UserRouter} />
        <Route exact path={privacyPolicyUrl} component={PrivacyPolicyView} />
        <Route exact path={serverErrorUrl} component={ServerErrorView} />
        <Route exact path={notFoundUrl} component={NotFoundView} />
        <Route exact path={offlineUrl} component={OfflineView} />
        <Route exact path={priceListUrl} component={PriceListView} />
        <Route exact path={directAccessUrl} component={DirectAccessView} />
        <Route component={NotFoundView} />
      </Switch>
    </Suspense>
  );
};
